import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  QuoteAuthor,
  QuoteColumn,
  QuoteFooter,
  QuoteCarouselItem,
  QuoteItemContainer,
  QuoteText,
  QuoteMain,
  QuoteRole,
  AutherDesc,
  QuoteSign
} from './index.style'
import gsap from 'gsap'
import { useInView } from 'react-intersection-observer'
import { animation } from '@styles/vars/animation.style'
import Container from '@components/Container'
import Spacer from '@components/Spacer'
import { Caption, Heading2Serif, TextBody2 } from '@components/TextStyles'
import IconArrowRight from '@svgs/IconArrowRight'
import { convertNewlineToBreak } from '@styles/utils/conversion.style'
import RichText from '@components/RichText'
import { GatsbyImage } from 'gatsby-plugin-image'

const Quote = ({ label, content }) => {
  const items = useRef([])
  const roles = useRef([])
  const parents = useRef([])
  const [mainHeight, setMainHeight] = useState()
  const [curEle, setCurEle] = useState()
  const [eleStyle, setEleStyle] = useState({ height: 'auto' })
  const [isSingleItem, setIsSingleItem] = useState(false)

  const [ref, inView] = useInView({
    rootMargin: animation.rootMargin,
  })
  const timeline = useRef()

  useEffect(() => {
    setIsSingleItem(content.length === 1)
  }, [content])

  useEffect(() => {
    window.addEventListener('resize', () => {
      if (!curEle) return

      const parent = parents.current[curEle]
      const moreHeight = window.innerWidth > 761 ? 310 : 170
      setMainHeight(parent.offsetHeight + moreHeight)
    })
  }, [])

  useEffect(() => {
    if (isSingleItem) {
      gsap.set([items.current[0], roles.current[0]], {
        opacity: 1,
        pointerEvents: 'all',
        y: 0
      });
      return;
    }

    const setupAnimation = () => {
      const yOffset = 10

      gsap.set([items.current, roles.current], {
        opacity: 0,
        y: yOffset,
        pointerEvents: 'none',
      })

      timeline.current = gsap.timeline({
        paused: true,
        repeat: -1,
      })

      items.current.forEach((item, itemIndex) => {
        const parent = parents.current[itemIndex]
        const role = roles.current[itemIndex]

        timeline.current
          .set([item, role], {
            opacity: 1,
            pointerEvents: 'all',
            onComplete: () => {
              setCurEle(itemIndex)
              const moreHeight = window.innerWidth > 761 ? 310 : 170
              setMainHeight(parent.offsetHeight + moreHeight)
            },
          })
          .to([item, role], {
            y: 0,
            ease: 'out',
            duration: 0.5,
          })
          .to([item, role], {
            yPercent: -yOffset,
            ease: 'in',
            duration: 0.4,
            delay: 14,
            onComplete: () => {
              gsap.set([item, role], {
                opacity: 0,
                yPercent: yOffset,
                pointerEvents: 'none',
              })
            },
          })
      })
    }

    setupAnimation()

    return () => {
      if (timeline.current) {
        timeline.current.kill()
      }
    }
  }, [isSingleItem])

  useEffect(() => {
    if (!isSingleItem && inView) {
      timeline.current.play()
    } else {
      timeline.current?.pause()
    }
  }, [inView, isSingleItem])

  useEffect(() => {
    setEleStyle({ height: mainHeight + 'px' })
  }, [mainHeight])

  return (
    <QuoteMain
      ref={ref}
      style={eleStyle}
      className={isSingleItem ? 'oneRotate' : ''}
    >
      <Container>
        <Spacer size={[60, 150]} />

        {label && <Caption as="h2">{label}</Caption>}

        <Spacer size={[30, 50]} />

        <QuoteFooter>
          {React.Children.toArray(
            content.map((item, itemIndex) => (
              <QuoteCarouselItem>
                <QuoteItemContainer
                  ref={ref => (parents.current[itemIndex] = ref)}
                >
                  <QuoteText ref={ref => (items.current[itemIndex] = ref)}>
                    <QuoteSign>“</QuoteSign>
                    <RichText
                      content={item.quoteContent}
                      paragraphSize="small"
                      maxWidth={38}
                      className='quoteContent'
                    />
                  </QuoteText>

                  <QuoteColumn ref={ref => (roles.current[itemIndex] = ref)}>
                    <QuoteAuthor>{item.authorName}</QuoteAuthor>

                    {item.authorDescription && (
                      <>
                        <Spacer size={5} />

                        <QuoteRole>
                          <AutherDesc>
                            {convertNewlineToBreak(
                              item.authorDescription.authorDescription
                            )}
                          </AutherDesc>
                          {item.image && (
                            <GatsbyImage
                              image={item.image.gatsbyImageData}
                              alt={item.image.description}
                            />
                          )}
                        </QuoteRole>
                      </>
                    )}
                  </QuoteColumn>
                </QuoteItemContainer>
              </QuoteCarouselItem>
            ))
          )}
        </QuoteFooter>

        <Spacer size={[60, 150]} />
      </Container>
    </QuoteMain>
  )
}

Quote.propTypes = {
  label: PropTypes.string,
  content: PropTypes.array,
}

export default Quote
